import React, { useState, useEffect } from 'react';
import { FaTools, FaTimes, FaMapMarkerAlt } from 'react-icons/fa';
import API from '../../API';
import { useOverlay } from '../../contexts/FocusContext';

interface Room {
  room_id: string;
  room_name: string;
  event_id: string;
  chat_room_arn: string;
  stage_arn: string;
  podium_position?: string;
}

interface ModToolsProps {
  accessToken: string;
  eventId: string;
  currentRoomId: string;
  onPodiumTeleport?: () => void; // Callback when user teleports to podium
}

const ModTools: React.FC<ModToolsProps> = ({ accessToken, eventId, currentRoomId, onPodiumTeleport }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [teleportConfirmRoom, setTeleportConfirmRoom] = useState<Room | null>(null);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Register as overlay when panel or confirm dialog is open - FocusContext handles Unity capture
  useOverlay('mod-tools', isOpen || teleportConfirmRoom !== null);

  // Fetch rooms when widget opens
  useEffect(() => {
    if (isOpen && accessToken && eventId) {
      fetchRooms();
    }
  }, [isOpen, accessToken, eventId]);

  const fetchRooms = async () => {
    setIsLoading(true);
    try {
      const response = await API.getRooms(accessToken, eventId);
      const data = await response.json();
      setRooms(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const parsePodiumPosition = (positionStr: string): { x: number; y: number; z: number } | null => {
    try {
      // Parse format like "(187.3, -0.5, 76.7)"
      const cleaned = positionStr.replace(/[()]/g, '').trim();
      const parts = cleaned.split(',').map(p => parseFloat(p.trim()));
      
      if (parts.length === 3 && parts.every(p => !isNaN(p))) {
        return { x: parts[0], y: parts[1], z: parts[2] };
      }
      return null;
    } catch (error) {
      console.error('Error parsing podium position:', error);
      return null;
    }
  };

  const handleTeleportClick = (room: Room) => {
    setTeleportConfirmRoom(room);
    setSaveMessage(null);
  };

  const handleConfirmTeleport = () => {
    if (!teleportConfirmRoom || !teleportConfirmRoom.podium_position) {
      return;
    }

    const position = parsePodiumPosition(teleportConfirmRoom.podium_position);
    
    if (!position) {
      setSaveMessage({ type: 'error', text: 'Invalid podium position format' });
      setTeleportConfirmRoom(null);
      return;
    }

    console.log('[ModTools→Unity] Teleporting to podium:', position);
    
    try {
      if (window.sendMessageToUnity) {
        const teleportData = {
          eventType: 'Teleport',
          payload: JSON.stringify({
            position: position,
            rotation: { x: 0, y: 0, z: 0 } // Default rotation
          })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportData));
        
        setSaveMessage({ type: 'success', text: 'Teleported to podium!' });
        setTimeout(() => setSaveMessage(null), 3000);
        
        // Notify parent that user is now at podium
        if (onPodiumTeleport) {
          onPodiumTeleport();
        }
      } else {
        setSaveMessage({ type: 'error', text: 'Unity bridge not available' });
      }
    } catch (error) {
      console.error('[ModTools→Unity] Error teleporting:', error);
      setSaveMessage({ type: 'error', text: 'Failed to teleport' });
    }
    
    setTeleportConfirmRoom(null);
  };

  const handleCancelTeleport = () => {
    setTeleportConfirmRoom(null);
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`px-4 py-2 rounded-lg transition-all duration-200 shadow-lg font-semibold text-white flex items-center gap-2 ${
          isOpen
            ? 'bg-yellow-700 bg-opacity-90'
            : 'bg-yellow-600 bg-opacity-80 hover:bg-opacity-100'
        }`}
        title="Moderator Tools"
      >
        <FaTools size={16} />
        Moderator
      </button>

      {/* ModTools Panel */}
      {isOpen && (
        <div
          className="absolute top-full right-0 mt-2 w-96 bg-[#1a1a1a] border border-purple-600 rounded-lg shadow-2xl z-50 flex flex-col max-h-[70vh]"
          style={{ zIndex: 10001 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#333] bg-purple-600 bg-opacity-20">
            <h3 className="text-white font-semibold text-lg flex items-center gap-2">
              <FaTools />
              Moderator Tools
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-purple-300 transition-colors"
            >
              <FaTimes size={18} />
            </button>
          </div>

          {/* Save Message */}
          {saveMessage && (
            <div className={`mx-4 mt-4 p-3 rounded-lg text-sm ${
              saveMessage.type === 'success' 
                ? 'bg-green-600 bg-opacity-20 border border-green-600 text-green-400'
                : 'bg-red-600 bg-opacity-20 border border-red-600 text-red-400'
            }`}>
              {saveMessage.text}
            </div>
          )}

          {/* Room List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            <div className="text-xs text-[#999] mb-2 uppercase font-semibold">
              Room Podium Positions
            </div>
            
            {isLoading ? (
              <div className="text-center text-[#666] py-8">Loading rooms...</div>
            ) : rooms.filter(room => room.podium_position && room.podium_position.trim() !== '').length === 0 ? (
              <div className="text-center text-[#666] py-8">No rooms with podium positions</div>
            ) : (
              rooms
                .filter(room => room.podium_position && room.podium_position.trim() !== '')
                .map((room) => {
                const isCurrentRoom = room.room_id === currentRoomId;
                const hasPodium = true; // Already filtered
                
                return (
                  <div
                    key={room.room_id}
                    className={`p-3 rounded-lg border transition-all duration-200 ${
                      isCurrentRoom
                        ? 'bg-purple-600 bg-opacity-10 border-purple-600'
                        : 'bg-[#2a2a2a] border-[#444]'
                    }`}
                  >
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium truncate">{room.room_name}</div>
                          <div className="text-xs text-[#666] mt-1">
                            {room.room_id}
                            {isCurrentRoom && <span className="ml-2 text-purple-400">(Current)</span>}
                          </div>
                        </div>
                      </div>
                      
                      {hasPodium ? (
                        <div className="space-y-2">
                          {isCurrentRoom && (
                            <button
                              onClick={() => handleTeleportClick(room)}
                              className="w-full px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-all duration-200 flex items-center justify-center gap-2"
                            >
                              <FaMapMarkerAlt size={12} />
                              Teleport to Podium
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-[#666] italic">
                          No podium position set
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Teleport Confirmation Popup */}
      {teleportConfirmRoom && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
          style={{ zIndex: 10002 }}
          onClick={handleCancelTeleport}
        >
          <div 
            className="bg-[#1a1a1a] border border-purple-600 rounded-lg shadow-2xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-600 bg-opacity-20 rounded-full">
                <FaMapMarkerAlt className="text-blue-400" size={24} />
              </div>
              <div>
                <h3 className="text-white text-lg font-semibold">
                  Teleport to Podium?
                </h3>
                <p className="text-[#999] text-sm mt-1">
                  {teleportConfirmRoom.room_name}
                </p>
              </div>
            </div>
            
            <div className="bg-[#2a2a2a] rounded-lg p-3 mb-6">
              <div className="text-xs text-[#999] mb-1">Position:</div>
              <div className="text-sm text-white font-mono">
                {teleportConfirmRoom.podium_position}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleConfirmTeleport}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 font-semibold flex items-center justify-center gap-2"
              >
                <FaMapMarkerAlt size={14} />
                Yes, Teleport
              </button>
              <button
                onClick={handleCancelTeleport}
                className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModTools;

